import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { FaFileInvoice, FaSpinner } from "react-icons/fa";
import { GradientLabel } from "./PageShell";
import {
  MENU_CLASS, optionClass, useAnchoredPosition, useOutsideClose, useListboxKeys, useScrollActiveIntoView,
} from "./dropdownKit";

const money = (v) => (Number(v) || 0).toLocaleString("en-US");

// Trình xem PDF của Chrome/Edge: ẩn toolbar + vừa chiều ngang -> mỗi hóa đơn trông
// như một trang A4 nối tiếp nhau. Fragment không gửi lên server nên không làm hỏng
// chữ ký presigned của S3.
const viewerSrc = (url) => `${String(url).split("#")[0]}#toolbar=0&navpanes=0&view=FitH`;

// Khi cuộn bằng code thì tạm tắt scroll-spy, nếu không dòng active sẽ nhảy qua
// từng hóa đơn nằm giữa đường cuộn.
const PROGRAMMATIC_SCROLL_MS = 800;

const idType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);
const itemShape = PropTypes.shape({
  id: idType.isRequired,
  label: PropTypes.string,
  meta: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pending: PropTypes.bool,
});

/** Menu "nhảy tới hóa đơn" — thay cho hàng tab cũ, không bị tràn khi nhiều hóa đơn. */
const JumpMenu = ({ items, activeId, onPick }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);

  const activeIdx = Math.max(0, items.findIndex((it) => it.id === activeId));
  const current = items[activeIdx];

  useOutsideClose(open, [btnRef, popRef], () => setOpen(false));

  const pick = (idx) => {
    const it = items[idx];
    if (!it) return;
    setOpen(false);
    btnRef.current?.focus();
    onPick(it.id);
  };

  const { activeIndex, setActiveIndex, onKeyDown } = useListboxKeys({
    open, setOpen, count: items.length, onSelect: pick, anchorRef: btnRef,
  });
  useScrollActiveIntoView(listRef, activeIndex);
  const coords = useAnchoredPosition(open, btnRef, { minWidth: 320, maxHeight: 360 });

  return (
    <>
      <div
        ref={btnRef}
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Jump to invoice"
        onKeyDown={onKeyDown}
        onClick={() => setOpen((o) => !o)}
        className={`tw-h-8 tw-max-w-[260px] tw-inline-flex tw-items-center tw-gap-2 tw-rounded-geist-sm tw-border tw-border-solid tw-px-2.5 tw-text-[12px] tw-select-none tw-cursor-pointer tw-bg-geist-bg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-geist-accent/20 ${
          open ? "tw-border-geist-accent" : "tw-border-geist-border hover:tw-border-geist-border-2"}`}
      >
        <i className="bi bi-list-ul tw-text-geist-fg-3 tw-flex-shrink-0" />
        <span className="tw-text-geist-fg-3 tw-tabular-nums tw-flex-shrink-0">{activeIdx + 1}/{items.length}</span>
        <span className="tw-font-semibold tw-text-geist-fg tw-truncate">{current?.label}</span>
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[10px] tw-text-geist-fg-3 tw-flex-shrink-0`} />
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", zIndex: 100060, ...coords, maxHeight: undefined, width: coords.width }}
          className={MENU_CLASS}
          onKeyDown={onKeyDown}
          role="presentation"
        >
          <div ref={listRef} role="listbox" style={{ maxHeight: coords.maxHeight }} className="tw-overflow-y-auto tw-py-1">
            {items.map((it, idx) => {
              const selected = it.id === activeId;
              return (
                <div
                  key={it.id}
                  data-index={idx}
                  role="option"
                  aria-selected={selected}
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => pick(idx)}
                  className={optionClass(selected, activeIndex === idx)}
                >
                  <span className="tw-w-5 tw-text-[11px] tw-text-geist-fg-3 tw-tabular-nums tw-flex-shrink-0">{idx + 1}</span>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-truncate">
                      {it.label}
                      {it.pending && <span className="tw-ml-1.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-text-[#a05e03]">not added</span>}
                    </span>
                    {it.meta && <span className="tw-block tw-truncate tw-text-[11px] tw-font-normal tw-text-geist-fg-3">{it.meta}</span>}
                  </span>
                  {!!Number(it.amount) && (
                    <span className="tw-text-sm tw-tabular-nums tw-text-primary tw-font-semibold tw-flex-shrink-0">{money(it.amount)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

JumpMenu.propTypes = {
  items: PropTypes.arrayOf(itemShape).isRequired,
  activeId: idType,
  onPick: PropTypes.func.isRequired,
};

/**
 * Pane xem hóa đơn dạng cuộn liên tục: mỗi hóa đơn là một khổ A4 xếp dọc (giống
 * một file PDF nhiều trang), thay cho kiểu tab mỗi lần chỉ xem được một hóa đơn.
 *
 * Vẫn nhúng iframe PDF (presigned S3) chứ không vẽ bằng pdf.js — bucket S3 không
 * bật CORS nên trình duyệt không fetch được bytes để vẽ canvas.
 *
 * Mặc định iframe có lớp phủ để con lăn chuột luôn cuộn cả chồng hóa đơn (trình
 * xem PDF trong iframe hay "nuốt" sự kiện cuộn). Bấm vào một hóa đơn để thao tác
 * với chính nó (zoom, chọn chữ); rê chuột ra ngoài thì trở lại chế độ cuộn.
 *
 * Props:
 *   items          [{ id, label, meta, amount, pending }] — pending = đang xem thử, chưa thêm
 *   activeId       hóa đơn đang chọn (đồng bộ với bảng Documents)
 *   onActiveChange(id) — khi người dùng cuộn tới / chọn trong menu / bấm vào hóa đơn
 *   resolveUrl(id) => Promise<url|null> — lấy view_url, chỉ gọi khi hóa đơn sắp lọt vào tầm nhìn
 *
 * Ref: { scrollTo(id) } — cuộn tới hóa đơn (chờ được nếu hóa đơn chưa render).
 */
const InvoicePreviewStack = forwardRef(function InvoicePreviewStack(
  { items = [], activeId = null, onActiveChange, resolveUrl }, ref
) {
  const scrollRef = useRef(null);
  const blockRefs = useRef({});
  const requested = useRef(new Set());
  const pendingScroll = useRef(null);
  const lockUntil = useRef(0);
  const [urls, setUrls] = useState({});          // id -> url | null (null = không tải được)
  const [interactiveId, setInteractiveId] = useState(null);

  // items là mảng mới sau mỗi render của form -> effect theo chuỗi id, đọc bản mới nhất qua ref.
  const latest = useRef({ items, activeId, onActiveChange, resolveUrl });
  latest.current = { items, activeId, onActiveChange, resolveUrl };
  const idsKey = items.map((it) => it.id).join("|");

  const loadUrl = useCallback((id) => {
    if (!id || requested.current.has(id)) return;
    requested.current.add(id);
    Promise.resolve(latest.current.resolveUrl?.(id))
      .then((url) => setUrls((u) => ({ ...u, [id]: url || null })))
      .catch(() => setUrls((u) => ({ ...u, [id]: null })));
  }, []);

  // Chỉ lấy URL cho hóa đơn sắp hiện ra — phiếu nhiều hóa đơn không bắn cả loạt request lúc mở.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || typeof IntersectionObserver === "undefined") {
      latest.current.items.forEach((it) => loadUrl(it.id));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) loadUrl(e.target.dataset.id); });
    }, { root, rootMargin: "600px 0px" });
    Object.values(blockRefs.current).forEach((node) => node && io.observe(node));
    return () => io.disconnect();
  }, [idsKey, loadUrl]);

  const scrollToId = useCallback((id, smooth = true) => {
    const root = scrollRef.current;
    const node = blockRefs.current[id];
    if (!root || !node) { pendingScroll.current = id; return; }
    pendingScroll.current = null;
    lockUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
    root.scrollTo({ top: Math.max(0, node.offsetTop - 12), behavior: smooth ? "smooth" : "auto" });
  }, []);

  useImperativeHandle(ref, () => ({ scrollTo: scrollToId }), [scrollToId]);

  // Hóa đơn vừa thêm chưa kịp render lúc được gọi scrollTo -> cuộn khi nó xuất hiện.
  useEffect(() => {
    if (pendingScroll.current) scrollToId(pendingScroll.current);
  }, [idsKey, scrollToId]);

  // Scroll-spy: hóa đơn nào có mép trên vượt qua 30% chiều cao pane là hóa đơn đang xem.
  const frame = useRef(0);
  const handleScroll = () => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const root = scrollRef.current;
      const { items: list, activeId: current, onActiveChange: notify } = latest.current;
      if (!root || !list.length || Date.now() < lockUntil.current) return;
      const probe = root.scrollTop + root.clientHeight * 0.3;
      let seen = list[0].id;
      list.forEach((it) => {
        const node = blockRefs.current[it.id];
        if (node && node.offsetTop <= probe) seen = it.id;
      });
      if (seen !== current) notify?.(seen);
    });
  };
  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const pick = (id) => {
    scrollToId(id);
    onActiveChange?.(id);
  };

  return (
    <>
      <div className="tw-px-4 tw-py-3 tw-border-b tw-border-solid tw-border-geist-border tw-flex-shrink-0 tw-flex tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-items-baseline tw-gap-2 tw-flex-shrink-0">
          <GradientLabel className="tw-mb-0 tw-text-sm">Invoice preview</GradientLabel>
          {items.length > 0 && <span className="tw-text-xs tw-text-geist-fg-3">{items.length}</span>}
        </div>
        {items.length > 0 && <JumpMenu items={items} activeId={activeId} onPick={pick} />}
      </div>

      <div ref={scrollRef} onScroll={handleScroll}
        className="tw-relative tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-bg-geist-bg-2 tw-px-4 tw-py-3">
        {items.length === 0 ? (
          <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-geist-fg-3 tw-p-6">
            <FaFileInvoice size={36} className="tw-mb-3 tw-text-geist-border-2" />
            <p className="tw-text-sm tw-m-0">Find an invoice to preview it here.</p>
          </div>
        ) : items.map((it, idx) => {
          const url = urls[it.id];
          const isActive = it.id === activeId;
          const interactive = interactiveId === it.id;
          return (
            <section
              key={it.id}
              data-id={it.id}
              ref={(node) => { if (node) blockRefs.current[it.id] = node; else delete blockRefs.current[it.id]; }}
              className="tw-mb-5 last:tw-mb-0"
            >
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5 tw-text-[12px] tw-min-w-0">
                <span className={`tw-inline-flex tw-h-5 tw-min-w-[20px] tw-items-center tw-justify-center tw-rounded tw-px-1 tw-text-[11px] tw-font-bold tw-tabular-nums tw-flex-shrink-0 ${
                  isActive ? "tw-bg-geist-accent tw-text-white" : "tw-bg-geist-100 tw-text-geist-fg-2"}`}>
                  {idx + 1}
                </span>
                <span className="tw-font-semibold tw-text-geist-fg tw-truncate tw-flex-shrink-0 tw-max-w-[45%]">{it.label}</span>
                {it.meta && <span className="tw-text-geist-fg-3 tw-truncate">{it.meta}</span>}
                {it.pending && (
                  <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-text-[#a05e03] tw-flex-shrink-0">Not added</span>
                )}
                <span className="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                  {!!Number(it.amount) && <span className="tw-tabular-nums tw-text-primary tw-font-semibold">{money(it.amount)}</span>}
                  {url && (
                    <a href={url} target="_blank" rel="noreferrer"
                      title="Open in a new tab (zoom, print, download)" aria-label={`Open ${it.label} in a new tab`}
                      className="tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-geist-sm tw-text-geist-fg-3 hover:tw-bg-geist-100 hover:tw-text-geist-accent">
                      <i className="bi bi-box-arrow-up-right tw-text-[12px]" />
                    </a>
                  )}
                </span>
              </div>

              {/* Khổ A4 (210 × 297). */}
              <div
                onMouseLeave={() => interactive && setInteractiveId(null)}
                className={`tw-relative tw-aspect-[210/297] tw-bg-white tw-rounded-sm tw-overflow-hidden tw-shadow-geist-card tw-border tw-border-solid ${
                  isActive ? "tw-border-geist-accent tw-ring-2 tw-ring-geist-accent/20" : "tw-border-geist-border"}`}
              >
                {url === undefined ? (
                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-geist-fg-3">
                    <FaSpinner className="tw-animate-spin tw-mr-2" /> Loading PDF…
                  </div>
                ) : url === null ? (
                  <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-sm tw-text-geist-fg-3 tw-p-6 tw-text-center">
                    <i className="bi bi-exclamation-triangle tw-text-[22px] tw-mb-2" />
                    Could not load this invoice from Doc Search.
                  </div>
                ) : (
                  <>
                    <iframe
                      title={`invoice-${it.label}`}
                      src={viewerSrc(url)}
                      loading="lazy"
                      className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-border-0"
                    />
                    {!interactive && (
                      <div
                        role="button"
                        tabIndex={-1}
                        aria-label={`Interact with ${it.label}`}
                        title="Click to zoom or select text in this invoice"
                        onClick={() => { setInteractiveId(it.id); if (!isActive) onActiveChange?.(it.id); }}
                        className="tw-absolute tw-inset-0 tw-cursor-pointer"
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
});

InvoicePreviewStack.propTypes = {
  items: PropTypes.arrayOf(itemShape),
  activeId: idType,
  onActiveChange: PropTypes.func,
  resolveUrl: PropTypes.func,
};

export default InvoicePreviewStack;
